/*
 * CredentialingHubPanel — single home for Overview + Submissions + Feed.
 *
 * Wraps the 3 existing credentialing screens into tabs so admins don't
 * have to guess which submenu holds which view.
 *
 * ?filter=expired | expiring_30d — Trust Ops → Credential health "?" deep-links
 *   here and pre-applies a banner-visible filter hint. Actual per-row filtering
 *   is delegated to CredentialingQueuePanel via the `initialFilter` prop.
 */
import React, { useState, Suspense } from "react";
import { useSearchParams } from "react-router-dom";

const C = { forest: "#2B4C3B", ink: "#0F172A", dim: "#475569", line: "#E2E0D8", amber: "#B45309" };

const CredentialingQueuePanel = React.lazy(() => import("./CredentialingQueuePanel"));
const CredentialingSubmissionsPanel = React.lazy(() => import("./CredentialingSubmissionsPanel"));
const CredentialingCoverage = React.lazy(() => import("./CredentialingCoverage"));
const ApplicationsPanel = React.lazy(() => import("./ApplicationsPanel"));

const TABS = [
  { id: "new", label: "New Applications" },
  { id: "overview", label: "Overview" },
  { id: "submissions", label: "Submissions" },
  { id: "coverage", label: "Coverage gaps" },
];

const FILTER_LABELS = {
  expired: "Expired credentials",
  expiring_30d: "Expiring within 30 days",
};

export default function CredentialingHubPanel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get("filter") || "";
  // A Trust Ops deep-link always means "show me the overview filtered" —
  // otherwise land on New Applications, the daily-workflow starting point
  // that used to be its own standalone sidebar item.
  const [tab, setTab] = useState(filter ? "overview" : "new");
  // Phase 2: after approving a TP/driver in New Applications, jump straight
  // into Submissions already opened on that exact record — "the next step
  // is obvious instead of something they have to go find."
  const [pendingFocus, setPendingFocus] = useState(null); // { kind, id }

  const handleApproved = (kind, id) => {
    setPendingFocus({ kind, id });
    setTab("submissions");
  };

  const clearFilter = () => {
    const p = new URLSearchParams(searchParams);
    p.delete("filter");
    setSearchParams(p);
  };

  return (
    <div className="space-y-4" data-testid="credentialing-hub">
      <div>
        <h1 className="text-2xl font-bold" style={{ color: C.ink }}>Credentialing</h1>
        <div className="text-sm" style={{ color: C.dim }}>One home from first application through MedexOva clearance — new applications, overview, submissions, and per-field gaps.</div>
      </div>

      {filter && FILTER_LABELS[filter] && (
        <div
          data-testid="credentialing-filter-banner"
          className="flex items-center gap-3 rounded-md px-4 py-2 text-sm"
          style={{ background: "#FEF3C7", border: `1px solid ${C.amber}`, color: C.amber }}
        >
          <span className="font-bold">Filtered:</span>
          <span>{FILTER_LABELS[filter]} — from Trust Ops → Credential health</span>
          <button
            type="button"
            onClick={clearFilter}
            data-testid="credentialing-filter-clear"
            className="ml-auto font-semibold text-xs underline"
          >Clear filter</button>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            data-testid={`credentialing-tab-${t.id}`}
            className="px-4 py-2 rounded-full text-sm font-semibold"
            style={{
              background: tab === t.id ? C.forest : "#fff",
              color: tab === t.id ? "#fff" : C.ink,
              border: `1px solid ${tab === t.id ? C.forest : C.line}`,
            }}>{t.label}</button>
        ))}
      </div>
      <Suspense fallback={<div className="p-8 text-sm text-center" style={{ color: C.dim }}>Loading…</div>}>
        {tab === "new" && <ApplicationsPanel embedded onApproved={handleApproved} />}
        {tab === "overview" && <CredentialingQueuePanel initialFilter={filter} />}
        {tab === "submissions" && (
          <CredentialingSubmissionsPanel
            initialFilter={filter}
            focusKind={pendingFocus?.kind}
            focusId={pendingFocus?.id}
            onFocusConsumed={() => setPendingFocus(null)}
          />
        )}
        {tab === "coverage" && <CredentialingCoverage />}
      </Suspense>
    </div>
  );
}
